
import React from 'react';
import Layout from '@/components/layout/Layout';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardHeader, CardContent, CardTitle } from '@/components/ui/card';
import ProductManagement from '@/components/settings/ProductManagement';
import CategoryManagement from '@/components/settings/CategoryManagement';
import { FileSpreadsheet, Users, Package, Settings as SettingsIcon, Shield, MessageSquare, Bell, AlertTriangle, Ruler } from 'lucide-react';
import UnitManagement from '@/components/settings/UnitManagement';
import { UserManagementPanel } from '@/components/users/UserManagementPanel';
import { RoleAccessManager } from '@/components/users/RoleAccessManager';
import TelegramSettings from '@/components/users/TelegramSettings';
import { TelegramGroupManager } from '@/components/users/TelegramGroupManager';
import { TelegramNotificationHistory } from '@/components/users/TelegramNotificationHistory';
import { useAuth } from '@/contexts/AuthContext';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { ApiTokenManager } from '@/components/settings/ApiTokenManager';

const Settings = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  return (
    <Layout>
      <div className="w-full">
        <h1 className="text-2xl font-bold mb-6">System Settings</h1>

        <Tabs defaultValue="users">
          <TabsList className="mb-4 flex flex-wrap gap-1">
            <TabsTrigger value="users">
              <Users className="h-4 w-4 mr-2" />
              Users
            </TabsTrigger>
            <TabsTrigger value="roles">
              <Shield className="h-4 w-4 mr-2" />
              Roles & Access
            </TabsTrigger>
            <TabsTrigger value="products">
              <Package className="h-4 w-4 mr-2" />
              Products
            </TabsTrigger>
            <TabsTrigger value="categories">
              <FileSpreadsheet className="h-4 w-4 mr-2" />
              Categories
            </TabsTrigger>
            <TabsTrigger value="units">
              <Ruler className="h-4 w-4 mr-2" />
              Units
            </TabsTrigger>
            <TabsTrigger value="telegram">
              <MessageSquare className="h-4 w-4 mr-2" />
              Telegram
            </TabsTrigger>
            <TabsTrigger value="notifications">
              <Bell className="h-4 w-4 mr-2" />
              Notifications
            </TabsTrigger>
            <TabsTrigger value="general">
              <SettingsIcon className="h-4 w-4 mr-2" />
              General
            </TabsTrigger>
            <TabsTrigger value="security">Security</TabsTrigger>
          </TabsList>


          <TabsContent value="users">
            <Card>
              <CardHeader>
                <CardTitle>User Management</CardTitle>
              </CardHeader>
              <CardContent>
                <UserManagementPanel />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="roles">
            <RoleAccessManager />
          </TabsContent>

          <TabsContent value="products">
            <Card>
              <CardHeader>
                <CardTitle>Product Management</CardTitle>
              </CardHeader>
              <CardContent>
                <ProductManagement />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="categories">
            <Card>
              <CardHeader>
                <CardTitle>Category Management</CardTitle>
              </CardHeader>
              <CardContent>
                <CategoryManagement />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="units">
            <UnitManagement />
          </TabsContent>

          <TabsContent value="telegram">
            <TelegramSettings />
          </TabsContent>

          <TabsContent value="notifications">
            <div className="space-y-6">
              {isAdmin && (
                <>
                  <TelegramGroupManager />
                </>
              )}

              <TelegramNotificationHistory />
            </div>
          </TabsContent>

          <TabsContent value="general">
            <Card>
              <CardHeader>
                <CardTitle>General Settings</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">General system settings will appear here.</p>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="security">
            {isAdmin ? (
              <ApiTokenManager />
            ) : (
              <Card>
                <CardHeader>
                  <CardTitle>Security Settings</CardTitle>
                </CardHeader>
                <CardContent>
                  <Alert>
                    <Shield className="h-4 w-4" />
                    <AlertTitle>Admin Access Required</AlertTitle>
                    <AlertDescription>
                      Security settings are only available to administrators.
                    </AlertDescription>
                  </Alert>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};

export default Settings;
